import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as suggestionActions from '../../../redux/actions/SuggestionsActions';
import * as filterActions from '../../../redux/actions/SidebarFilterActions';
import { deepEquals, formatReferenceOptions } from '../../../lib/utils';
import { PermissionTypes } from '../../../lib/systemVariables';
import { TableNames, PhoneNumberTypes } from '../../../lib/constants';
import { toastr } from 'react-redux-toastr';
import { HotKeys } from 'react-hotkeys';

import NavigationComponent from '../util/NavigationComponent';
import CustomDropdown from '../util/CustomDropdown';
import SaveSearchModal from './SaveSearchModal';
import AutoSuggestMultiInput from '../util/AutoSuggestMultiInput';
import AutocompleteMultiInput from '../util/AutocompleteMultiInput';
import FreeTextEntry from '../util/FreeTextEntry';
import MultiSelect from '../util/MultiSelectToggleView';
import DateRangePicker from '../util/DateRangePicker';
import ValidatedPhoneInput from '../util/ValidatedPhoneInput';
import LoadingSpinner from '../util/LoadingSpinner';

class SidebarFilter extends NavigationComponent {
    constructor(props) {
        super(props);

        this.state = {
            showModals: {
                saveSearchModal: false
            }
        }

        this.update = this.update.bind(this);
    }

    componentWillMount() {
        this.props.actions.getSavedSearches(this.props.type);
    }

    update(propertyName, value) {
        this.props.actions.updateFilters(this.props.type, { [propertyName]: value });
    }

    search() {
        if (!deepEquals(this.props.filters, this.props.appliedFilters)) {
            this.props.actions.setAppliedFilters(this.props.type, this.props.filters);
            this.props.search(this.props.filters);
        }
    }

    async saveSearch(title) {
        await this.props.actions.createSavedSearch(this.props.type, title, this.props.filters);
        if (this.props.newSearch.success) {
            toastr.success(`${title} search has been saved.`, { timeOut: 2000, progressBar: false });
        }
    }

    async deleteSearch(searchId, title) {
        await this.props.actions.deleteSavedSearch(this.props.type, searchId);
        if (!this.props.savedSearchList.deleting.hasOwnProperty(searchId)) {
            toastr.success(`${title} search has been deleted.`, { timeOut: 2000, progressBar: false });
        } else {
            toastr.error("Something went wrong", `This action could not be completed.  Please try again.`);
        }
    }

    render() {
        const noFilters =
            !this.props.filters.keyword 
            && !this.props.filters.reportName 
            && !this.props.filters.teudatZahut  
            && !this.props.filters.bankAccount
            && !this.props.filters.firstName 
            && !this.props.filters.lastName 
            && !this.props.filters.streetNames.length 
            && !this.props.filters.cities.length 
            && !this.props.filters.phoneNumber 
            && !this.props.filters.categories.length
            && !this.props.filters.nationalities.length 
            && !this.props.filters.recipientTypes.length
            && !this.props.filters.recipientStatuses.length 
            && !this.props.filters.organizations.length 
            && !this.props.filters.taxId
            && !this.props.filters.references.length
            && !this.props.filters.hasAchAccount
            && (this.props.type === TableNames.Recipients
                ? !this.props.filters.recipientSearch && !this.props.filters.hasPledges
                    && !this.props.filters.incomplete && !this.props.filters.campaignLists.length
                : (!this.props.filters.recipients.length && !this.props.filters.reportMemo && !this.props.filters.checkNumber
                    && !this.props.filters.applicationStatuses.length && !this.props.filters.applicationReasons.length
                    && !this.props.filters.approvers.length && !this.props.filters.sources.length
                    && !this.props.filters.funds.length && !this.props.filters.campaigns.length
                    && !this.props.filters.campaignIndividualCategories.length && !this.props.filters.campaignLists.length
                    && (!this.props.filters.startDate || !this.props.filters.endDate)
                    && this.props.filters.hasReceipt === null));

        const canSearch = !deepEquals(this.props.filters, this.props.appliedFilters);

        return (
            <div>
                <aside className={`sidebar ${this.props.visible ? "sidebar-show" : "sidebar-hidden"}`}>
                    {this.props.loading && <LoadingSpinner height="32" />}
                    <div className="sidebar-header relative">
                        <p className="medium-text uppercase-text accent-text"> {this.props.type} FILTER</p>
                        <p className={`link-text xs-text ${noFilters ? "disabled" : ""}`} onClick={() => { if (!noFilters) this.props.actions.resetFilters(this.props.type) }}>Clear All</p>
                        <i className="icon ion-arrow-left-c sidebar-slide-in" onClick={() => this.props.actions.toggleSidebar(this.props.type)}></i>
                    </div>
                    <HotKeys
                        keyMap={{ search: 'enter' }}
                        handlers={{ search: () => { if (this.props.visible) this.search() } }}
                    >
                        <div className="sidebar-content">
                            <CustomDropdown
                                placeholder="Saved Searches"
                                options={this.props.savedSearchList.data}
                                onSelect={searchId => this.props.actions.getSavedSearch(this.props.type, searchId)}
                                selection={this.props.savedSearch.data.value}
                                actionIcon={<i className="icon ion-android-delete action"></i>}
                                action={this.deleteSearch.bind(this)}
                                loading={this.props.savedSearch.loading} />

                            <input type="text" placeholder="Keyword" value={this.props.filters.keyword || ''}
                                onChange={e => this.update("keyword", e.target.value)} />

                            {this.props.type === TableNames.Applications
                                ? <AutoSuggestMultiInput title="Name" suggestions={this.props.recipientSuggestions.data}
                                    suggestionKey="applicantId" suggestionValue="name" allowIncomplete={true}
                                    getSuggestions={this.props.actions.getRecipientSuggestions} clearSuggestions={this.props.actions.clearRecipientSuggestions}
                                    loadingSuggestions={this.props.recipientSuggestions.loading}
                                    name="recipients" selection={this.props.filters.recipients} update={this.update} />
                                : <FreeTextEntry title="Recipient Search" value={this.props.filters.recipientSearch}
                                    propertyName="recipientSearch" update={this.update} />
                            }

                            <div className="modal-row">
                                <div className="column-half">
                                    <FreeTextEntry title="First Name" value={this.props.filters.firstName}
                                        propertyName="firstName" update={this.update} />
                                </div>
                                <div className="column-half">
                                    <FreeTextEntry title="Last Name" value={this.props.filters.lastName}
                                        propertyName="lastName" update={this.update} />
                                </div>
                            </div>
                            
                            <FreeTextEntry title="Report Name" value={this.props.filters.reportName}
                                propertyName="reportName" update={this.update} />

                            {this.props.type === TableNames.Applications &&
                                <FreeTextEntry title="Report Memo" value={this.props.filters.reportMemo}
                                    propertyName="reportMemo" update={this.update} />
                            }

                            <FreeTextEntry title="Teudat Zehut" value={this.props.filters.teudatZahut}
                                propertyName="teudatZahut" update={this.update} />
                            
                            {this.props.type === TableNames.Recipients &&
                            <FreeTextEntry title="Bank Account" value={this.props.filters.bankAccount}
                                propertyName="bankAccount" update={this.update} />
                            }

                            <div className="modal-row">
                                <div className="street-num column-third">
                                    <FreeTextEntry title="Num" value={this.props.filters.streetNum}
                                        propertyName="streetNum" update={this.update} />
                                </div>
                                <div className="street-name">
                                    <AutocompleteMultiInput title="Street Name" name="streetNames"
                                        options={this.props.systemSettings.otherSettings.streetNames.map(s => ({ key: s, value: s }))}
                                        selection={this.props.filters.streetNames} update={this.update} />
                                </div>
                            </div>

                            <AutocompleteMultiInput title="City" name="cities"
                                options={this.props.systemSettings.otherSettings.cities.map(c => ({ key: c, value: c }))}
                                selection={this.props.filters.cities} update={this.update} />

                            <ValidatedPhoneInput
                                phoneNumber={this.props.filters.phoneNumber || ''}
                                phoneType={this.state.phoneType || PhoneNumberTypes.American}
                                phoneNumberName="phoneNumber"
                                phoneTypeName="phoneType"
                                onChange={(name, value) => name === "phoneNumber" ? this.update(name, value) : this.setState({ [name]: value })} />

                            {this.props.userPermissions.institutions && <div>
                                <AutocompleteMultiInput title="Organization" name="organizations"
                                    options={this.props.tags.organizations}
                                    selection={this.props.filters.organizations} update={this.update} />
                                <FreeTextEntry title="Tax ID" value={this.props.filters.taxId}
                                    propertyName="taxId" update={this.update} />
                            </div> }

                            {this.props.type === TableNames.Applications && <div>
                                <FreeTextEntry title="Check" value={this.props.filters.checkNumber}
                                    propertyName="checkNumber" update={this.update} />
                                    
                                <div className="custom-check-container">
                                    <input
                                        type="checkbox"
                                        id="hasAchAccount"
                                        name="hasAchAccount"
                                        className="custom-check"
                                        checked={this.props.filters.hasAchAccount || false}
                                        onChange={e => this.update('hasAchAccount', !this.props.filters.hasAchAccount)}
                                    />
                                    <label htmlFor="hasAchAccount">Has ACH Account</label>
                                    <br />
                                    <br />
                                </div>
                            </div>
                            }

                            {this.props.type === TableNames.Recipients && <div>
                                <div className="custom-check-container">
                                    <input
                                        type="checkbox"
                                        id="pledge-checkbox"
                                        name="pledge-checkbox"
                                        className="custom-check"
                                        checked={this.props.filters.hasPledges || false}
                                        onChange={e => this.update('hasPledges', !this.props.filters.hasPledges)}
                                    />
                                    <label htmlFor="pledge-checkbox" className="secondary-text">Show Pledges Only</label>
                                    <br />
                                    <br />
                                </div>
                                <div className="custom-check-container">
                                    <input
                                        type="checkbox"
                                        id="incomplete-checkbox"
                                        name="incomplete-checkbox"
                                        className="custom-check"
                                        checked={this.props.filters.incomplete || false}
                                        onChange={e => this.update('incomplete', !this.props.filters.incomplete)}
                                    />
                                    <label htmlFor="incomplete-checkbox" className="secondary-text">Show Incomplete Only</label>
                                    <br />
                                    <br />
                                </div>
                            </div>}

                            <MultiSelect title="Category" propertyName="categories"
                                options={[
                                    ...this.props.systemSettings.recipientSettings.individualTags,
                                    ...this.props.systemSettings.recipientSettings.organizationTags
                                ]}
                                selection={this.props.filters.categories} update={this.update} />

                            <MultiSelect title="Nationality" propertyName="nationalities"
                                options={this.props.systemSettings.otherSettings.nationalities.map(n => ({ key: n, value: n }))}
                                selection={this.props.filters.nationalities} update={this.update} />

                            {(this.props.userPermissions.individuals && this.props.userPermissions.institutions) && (
                                <MultiSelect title="Recipient Type" propertyName="recipientTypes"
                                    options={this.props.systemSettings.otherSettings.recipientTypes}
                                    selection={this.props.filters.recipientTypes} update={this.update} />
                            )}

                            <MultiSelect title="Recipient Status" propertyName="recipientStatuses"
                                options={this.props.systemSettings.otherSettings.recipientStatuses}
                                selection={this.props.filters.recipientStatuses} update={this.update} />

                            {this.props.type === TableNames.Applications && <div>
                                <MultiSelect title="Application Status" propertyName="applicationStatuses"
                                    options={this.props.systemSettings.otherSettings.applicationStatuses}
                                    selection={this.props.filters.applicationStatuses} update={this.update} />
                                {(this.props.userPermissions.individuals) && (
                                    <MultiSelect title="Individual Application Reason" propertyName="applicationReasons"
                                        options={this.props.systemSettings.applicationSettings.individualApplicationReasons}
                                        selection={this.props.filters.applicationReasons} update={this.update} />
                                )}
                                {(this.props.userPermissions.institutions) && (
                                    <MultiSelect title="Organization Application Reason" propertyName="applicationReasons"
                                        options={this.props.systemSettings.applicationSettings.organizationApplicationReasons}
                                        selection={this.props.filters.applicationReasons} update={this.update} />
                                )}
                                <AutocompleteMultiInput title="Approver" name="approvers"
                                    options={this.props.tags.approvers}
                                    selection={this.props.filters.approvers} update={this.update} />
                                <AutocompleteMultiInput title="Fund" name="funds"
                                    options={this.props.tags.funds}
                                    selection={this.props.filters.funds} update={this.update} />
                                <AutocompleteMultiInput title="Campaign" name="campaigns"
                                    options={this.props.tags.campaigns}
                                    selection={this.props.filters.campaigns} update={this.update} />
                                <AutocompleteMultiInput title="Campaign Individual Category" name="campaignIndividualCategories"
                                    options={this.props.systemSettings.campaignSettings.campaignIndivCategories}
                                    selection={this.props.filters.campaignIndividualCategories} update={this.update} />
                                <AutocompleteMultiInput title="Campaign List" name="campaignLists"
                                    options={this.props.tags.campaignLists}
                                    selection={this.props.filters.campaignLists} update={this.update} />
                                <AutocompleteMultiInput title="Source" name="sources"
                                    options={formatReferenceOptions(this.props.tags.references)}
                                    selection={this.props.filters.sources} update={this.update} />
                            </div>}

                            {this.props.type === TableNames.Recipients &&
                                <AutocompleteMultiInput title="Campaign List" name="campaignLists"
                                    options={this.props.tags.campaignLists}
                                    selection={this.props.filters.campaignLists} update={this.update} />
                            }

                            <AutocompleteMultiInput title="Reference / Solicitor / Rosh Yeshiva" name="references"
                                options={formatReferenceOptions(this.props.tags.references)}
                                selection={this.props.filters.references} update={this.update} />

                            {this.props.type === TableNames.Applications &&
                                <DateRangePicker
                                    startDate={this.props.filters.startDate}
                                    endDate={this.props.filters.endDate}
                                    update={this.update} />}

                            {this.props.type === TableNames.Applications &&
                                <div className="filter-section">
                                    <label className="input-label">Receipt</label>
                                    <div className="filter-chips">
                                        <div
                                            className={`filter-chip ${this.props.filters.hasReceipt === true ? 'filter-chip-selected' : ''}`}
                                            onClick={() => this.update('hasReceipt', this.props.filters.hasReceipt === true ? null : true)}
                                        >
                                            Yes
                                        </div>
                                        <div
                                            className={`filter-chip ${this.props.filters.hasReceipt === false ? 'filter-chip-selected' : ''}`}
                                            onClick={() => this.update('hasReceipt', this.props.filters.hasReceipt === false ? null : false)}
                                        >
                                            No
                                        </div>
                                    </div>
                                </div>}

                        </div>
                        <div className="sidebar-footer">
                            <button className="btn btn-accent btn-fullWidth" onClick={this.toggleModal.bind(this, "saveSearchModal")}
                                disabled={noFilters || this.props.savedSearch.data.key}>Save</button>
                            <button className="btn btn-accent2 btn-fullWidth" onClick={this.search.bind(this)}
                                disabled={!canSearch}>Search</button>
                        </div>
                    </HotKeys>
                </aside>

                <SaveSearchModal
                    modalShowing={this.state.showModals.saveSearchModal}
                    close={this.toggleModal.bind(this, "saveSearchModal")}
                    save={this.saveSearch.bind(this)}
                    saving={this.props.newSearch.loading}
                    success={this.props.newSearch.success}
                    savedSearchTitles={this.props.savedSearchList.data.map(search => search.value)} />
            </div>
        );
    }
}

function mapStateToProps(state, ownProps) {
    const filterProps = ownProps.type === TableNames.Applications ? state.applicationsSidebarFilters : state.recipientsSidebarFilters;
    return {
        loading: state.settings.systemSettings.loading,
        tags: {
            campaigns: state.campaigns.campaigns.data,
            campaignLists: state.campaignLists.allCampaignLists.data,
            organizations: state.recipientAccounts.organizations.data,
            approvers: state.users.approvers.data,
            funds: state.funds.fundsByDept.all,
            references: state.references.references.data
        },
        systemSettings: state.settings.systemSettings.data,
        visible: filterProps.showSidebar,
        filters: filterProps.filters,
        appliedFilters: filterProps.appliedFilters,
        savedSearchList: filterProps.savedSearchList,
        savedSearch: filterProps.savedSearch,
        newSearch: filterProps.newSearch,
        recipientSuggestions: state.suggestions.recipientSuggestions,
        userPermissions: {
            individuals: state.userProfile.permissions.data.indexOf(PermissionTypes.IndividualsManage) >= 0,
            institutions: state.userProfile.permissions.data.indexOf(PermissionTypes.InstitutionsManage) >= 0
        }
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({ ...suggestionActions, ...filterActions }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(SidebarFilter);


// WEBPACK FOOTER //
// ./src/components/commonComponents/shared/SidebarFilter.jsx